import type { BusinessSummary } from '../contexts/AuthTypes';

/**
 * Subscription statuses that mean the business has a live subscription of SOME kind.
 *
 * `trialing` counts — a trial has the paid plan's access — while `canceled`, `incomplete` and an
 * absent status do not. Only a fallback now: see below.
 */
const LIVE_PAID_STATUSES = new Set(['active', 'trialing', 'past_due', 'unpaid']);

/**
 * Whether this business has the DA intelligence side of the product — search, saved applications,
 * alerts — as opposed to the free marketplace listing alone.
 *
 * ONE definition on purpose. The plan page's "current plan" badge, the sidebar's PRO badges and the
 * top bar's search box all ask this question, and copies of it would eventually disagree.
 *
 * This is a PRESENTATION check — what to offer, not what to permit. The server refuses the request
 * itself (PlanEntitlements.cs); anyone can still reach a route by typing its URL.
 *
 * <h3>Why it prefers a server-sent flag</h3>
 * It used to read `subscriptionStatus` alone, because "the plan names do not exist in Stripe yet".
 * They do now — and the day the $0 Marketplace plan shipped, that check broke: every signup is put
 * on a real Stripe subscription whose status is `active`, so a business paying nothing looked
 * exactly like one paying $29.99, and the whole DA product was on offer to everybody.
 *
 * Telling them apart needs the PRICE, and the price ids are server configuration the browser has no
 * business holding. So the server computes it (`hasDaIntelligence` on the business summary) and
 * this reads that. The status check survives only as a fallback for a payload from an older server
 * that does not send the flag — wrong in the same direction it always was, but never wrong for a
 * client that is talking to a current server.
 */
export function hasDaIntelligence(business: BusinessSummary | null | undefined): boolean {
    if (typeof business?.hasDaIntelligence === 'boolean') return business.hasDaIntelligence;
    return LIVE_PAID_STATUSES.has(business?.subscriptionStatus ?? '');
}

/**
 * What to call the plan a business is on, in customer-facing UI.
 *
 * DERIVED, never the stored `planName`. That field holds whatever Stripe product the business was
 * sold — "Exclusive", "List", "Basic" — all of which are the retired per-council catalogue. The
 * product is two plans now: the free Marketplace listing, and Intelligence on top of it. Showing a
 * subscriber "Exclusive" names something they cannot buy and we no longer sell.
 *
 * Same predicate as everything else that asks this question, so the sidebar, the plan page and the
 * top bar's search box cannot disagree about which plan someone is on.
 *
 * ADMIN SCREENS ARE DIFFERENT and deliberately not changed: Subscriptions and SubscriptionDetails
 * show the real stored `planName`, because an operator reconciling an invoice needs the product
 * that was actually charged, not the name we market it under today.
 */
export function planDisplayName(business: BusinessSummary | null | undefined): string {
    return hasDaIntelligence(business) ? 'INTELLIGENCE PLAN' : 'MARKETPLACE PLAN';
}
